import React from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const createCustomIcon = (color, emoji) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        background-color: ${color};
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        border: 3px solid white;
      ">
        ${emoji}
      </div>
    `,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20]
  });
};

const deliveryIcon = createCustomIcon('#22C55E', '📍');
const driverIcon = createCustomIcon('#22C55E', '🚴');
const restaurantIcon = createCustomIcon('#F97316', '🍔');

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

function OrderMap({ order, driverLocation }) {
  if (!order?.delivery_lat || !order?.delivery_lng) return null;

  const deliveryLat = parseFloat(order.delivery_lat);
  const deliveryLng = parseFloat(order.delivery_lng);
  const driverLat = driverLocation?.lat;
  const driverLng = driverLocation?.lng;
  const branchLat = order.branch_lat ? parseFloat(order.branch_lat) : null;
  const branchLng = order.branch_lng ? parseFloat(order.branch_lng) : null;

  let centerLat = deliveryLat;
  let centerLng = deliveryLng;
  let zoom = 13;

  if (driverLat && driverLng) {
    centerLat = (deliveryLat + driverLat) / 2;
    centerLng = (deliveryLng + driverLng) / 2;
    const latDiff = Math.abs(deliveryLat - driverLat);
    const lngDiff = Math.abs(deliveryLng - driverLng);
    const maxDiff = Math.max(latDiff, lngDiff);
    if (maxDiff > 0.1) zoom = 11;
    else if (maxDiff > 0.05) zoom = 12;
    else zoom = 13;
  }

  const distance = driverLat && driverLng
    ? calculateDistance(driverLat, driverLng, deliveryLat, deliveryLng)
    : null;
  const estimatedTime = distance ? Math.round(distance * 2) : null;

  return (
    <div className="order-map-container" style={{ marginTop: '15px', marginBottom: '15px' }}>
      <div style={{
        padding: '10px',
        background: 'white',
        borderRadius: '8px 8px 0 0',
        borderBottom: '1px solid #e0e0e0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <strong style={{ color: '#333', fontSize: '14px' }}>📍 Dirección de entrega</strong>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>
            {order.delivery_address}
          </p>
        </div>
        {distance && (
          <div style={{
            textAlign: 'right',
            background: 'linear-gradient(135deg, #22C55E 0%, #15803D 100%)',
            padding: '8px 12px',
            borderRadius: '20px',
            color: 'white',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            <div>{distance.toFixed(1)} km</div>
            {estimatedTime && <div style={{ fontSize: '10px', opacity: 0.9 }}>~{estimatedTime} min</div>}
          </div>
        )}
      </div>
      <MapContainer
        center={[centerLat, centerLng]}
        zoom={zoom}
        style={{ height: '260px', width: '100%', borderRadius: '0 0 12px 12px', zIndex: 0 }}
        scrollWheelZoom={true}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={[deliveryLat, deliveryLng]} icon={deliveryIcon}>
          <Popup>
            <div style={{ textAlign: 'center', padding: '5px' }}>
              <strong style={{ color: '#22C55E', fontSize: '14px' }}>📍 Entrega</strong><br />
              <span style={{ fontSize: '12px', color: '#666' }}>{order.delivery_address}</span>
            </div>
          </Popup>
        </Marker>
        {driverLat && driverLng && (
          <Marker position={[driverLat, driverLng]} icon={driverIcon}>
            <Popup>
              <div style={{ textAlign: 'center', padding: '5px' }}>
                <strong style={{ color: '#22C55E', fontSize: '14px' }}>🚴 Tu ubicación</strong><br />
                <span style={{ fontSize: '12px', color: '#666' }}>Posición actual</span>
                {distance && (
                  <div style={{ marginTop: '5px', fontSize: '11px', color: '#22C55E', fontWeight: 'bold' }}>
                    {distance.toFixed(1)} km de distancia
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        )}
        {branchLat && branchLng && (
          <Marker position={[branchLat, branchLng]} icon={restaurantIcon}>
            <Popup>
              🍔 Restaurante: {order.restaurant_name}
            </Popup>
          </Marker>
        )}
        {driverLat && driverLng && (
          <Polyline
            positions={
              branchLat != null && branchLng != null
                ? [
                    [driverLat, driverLng],
                    [branchLat, branchLng],
                    [deliveryLat, deliveryLng]
                  ]
                : [
                    [driverLat, driverLng],
                    [deliveryLat, deliveryLng]
                  ]
            }
            color="#22C55E"
            weight={4}
            opacity={0.8}
            dashArray="10, 10"
          />
        )}
      </MapContainer>
    </div>
  );
}

export default OrderMap;
